import React, { useState } from 'react';
import { SceneState, useSceneStore } from '../../state/sceneStore';
import { Move, RotateCw, Maximize, MousePointer2, Check, X } from 'lucide-react';

const ReferenceImageAlignmentPanel: React.FC = () => {
  const {
    isReferenceImageAlignMode,
    setIsReferenceImageAlignMode,
    referenceImageAlignMethod,
    setReferenceImageAlignMethod,
    referenceImageAlignMode,
    setReferenceImageAlignMode,
    pointPickingStep,
    setPointPickingStep,
    pickingState,
    setPickingState,
    setUseOrthographic,
    setSelectedObjectId,
    referenceImageConfig,
    setReferenceImageConfig,
    sensors,
    obstacles,
    referenceImageScaleSensors,
    setReferenceImageScaleSensors,
    referenceImageScaleObstacles,
    setReferenceImageScaleObstacles,
    applyReferenceImageScaleToScene,
  } = useSceneStore();

  const [scaleDistanceInput, setScaleDistanceInput] = useState<string>('1.0');

  const hasSensors = sensors.length > 0;
  const hasObstacles = obstacles.length > 0;
  const showSceneScaleOptions = hasSensors || hasObstacles;

  const sceneScaleOptions = showSceneScaleOptions ? (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        fontSize: '13px',
        color: '#cbd5e1',
        backgroundColor: '#1e293b',
        padding: '10px',
        borderRadius: '6px',
      }}
      data-testid="reference-image-scene-scale-options"
    >
      <span style={{ fontWeight: 500, color: '#e2e8f0' }}>Move/scale scene objects with image</span>
      {hasSensors && (
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={referenceImageScaleSensors}
            onChange={(event) => setReferenceImageScaleSensors(event.target.checked)}
            data-testid="reference-image-scale-sensors"
          />
          Sensors (X/Y position only)
        </label>
      )}
      {hasObstacles && (
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={referenceImageScaleObstacles}
            onChange={(event) => setReferenceImageScaleObstacles(event.target.checked)}
            data-testid="reference-image-scale-obstacles"
          />
          Obstacles (X/Y position and footprint only)
        </label>
      )}
    </div>
  ) : null;

  if (!isReferenceImageAlignMode) return null;

  const handleClose = () => {
    setIsReferenceImageAlignMode(false);
    setReferenceImageAlignMode(null);
    setPointPickingStep(null);
    setPickingState({ origin: null, xAxisEnd: null, scalePointA: null, scalePointB: null });
    setSelectedObjectId(null);
    setUseOrthographic(false);
  };

  const handleMethodChange = (method: 'interactive' | 'point-picking') => {
    setReferenceImageAlignMethod(method);
    if (method === 'interactive') {
      setReferenceImageAlignMode('translate');
      setPointPickingStep(null);
      setPickingState({ origin: null, xAxisEnd: null, scalePointA: null, scalePointB: null });
      setSelectedObjectId('reference-image');
    } else {
      setReferenceImageAlignMode(null);
      setPointPickingStep('origin');
      setPickingState({ origin: null, xAxisEnd: null, scalePointA: null, scalePointB: null });
      setSelectedObjectId(null);
    }
  };

  const handleApplyScaleDistance = () => {
    const dist = parseFloat(scaleDistanceInput);
    if (!isNaN(dist) && dist > 0) {
      if (pickingState.scalePointA && pickingState.scalePointB && pickingState.origin) {
        const { origin, xAxisEnd, scalePointA, scalePointB } = pickingState;
        
        let pos = [0, 0, 0];
        let rot = [0, 0, 0];
        let scl = [1, 1, 1];
        if (referenceImageConfig.transform) {
          pos = referenceImageConfig.transform.position;
          rot = referenceImageConfig.transform.rotation;
          scl = referenceImageConfig.transform.scale;
        }

        // 1. Translate so origin is at (0,0)
        let newPx = pos[0] - origin[0];
        let newPy = pos[1] - origin[1];
        let newRotZ = rot[2];

        // 2. Rotate so xAxisEnd is on X axis
        if (xAxisEnd) {
          const vx = xAxisEnd[0] - origin[0];
          const vy = xAxisEnd[1] - origin[1];
          const angle = Math.atan2(vy, vx);
          
          const cos = Math.cos(-angle);
          const sin = Math.sin(-angle);
          
          const rx = newPx * cos - newPy * sin;
          const ry = newPx * sin + newPy * cos;
          newPx = rx;
          newPy = ry;
          newRotZ = rot[2] - angle;
        }

        // 3. Scale
        const currentDistance = Math.sqrt((scalePointA[0] - scalePointB[0]) ** 2 + (scalePointA[1] - scalePointB[1]) ** 2);
        if (currentDistance > 0) {
          const scaleFactor = dist / currentDistance;
          newPx *= scaleFactor;
          newPy *= scaleFactor;
          
          setReferenceImageConfig({ 
            transform: {
              position: [newPx, newPy, pos[2]],
              rotation: [rot[0], rot[1], newRotZ],
              scale: [scl[0] * scaleFactor, scl[1] * scaleFactor, scl[2]]
            }
          });

          if (referenceImageScaleSensors || referenceImageScaleObstacles) {
            const sceneState = useSceneStore.getState();
            applyReferenceImageScaleToScene(
              sceneState.sensors,
              sceneState.obstacles,
              [pos[0], pos[1]],
              [newPx, newPy],
              scaleFactor,
              {
                scaleSensors: referenceImageScaleSensors,
                scaleObstacles: referenceImageScaleObstacles,
              },
            );
          }
          
          setPickingState({ origin: null, xAxisEnd: null, scalePointA: null, scalePointB: null });
          setPointPickingStep(null);
          handleMethodChange('interactive');
        }
      }
    }
  };

  const handleUndo = () => {
    if (pointPickingStep === 'xaxis') {
      setPickingState({ origin: null });
      setPointPickingStep('origin');
    } else if (pointPickingStep === 'scale-1') {
      setPickingState({ xAxisEnd: null });
      setPointPickingStep('xaxis');
    } else if (pointPickingStep === 'scale-2') {
      setPickingState({ scalePointA: null });
      setPointPickingStep('scale-1');
    } else if (pointPickingStep === 'scale-distance') {
      setPickingState({ scalePointB: null });
      setPointPickingStep('scale-2');
    }
  };

  const panelStyle: React.CSSProperties = {
    position: 'absolute',
    top: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(15, 23, 42, 0.9)',
    border: '1px solid #334155',
    borderRadius: '8px',
    padding: '15px',
    color: 'white',
    zIndex: 100,
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    minWidth: '350px',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #334155',
    paddingBottom: '10px',
    margin: 0,
  };

  const buttonGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: '5px',
    backgroundColor: '#1e293b',
    padding: '4px',
    borderRadius: '6px',
  };

  const getButtonStyle = (isActive: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '8px 12px',
    backgroundColor: isActive ? '#3b82f6' : 'transparent',
    color: isActive ? 'white' : '#94a3b8',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: 500,
    transition: 'all 0.2s',
  });

  const instructionsStyle: React.CSSProperties = {
    fontSize: '13px',
    color: '#cbd5e1',
    lineHeight: '1.5',
    backgroundColor: '#1e293b',
    padding: '10px',
    borderRadius: '6px',
    borderLeft: '3px solid #3b82f6',
  };

  return (
    <div style={panelStyle}>
      <div style={headerStyle}>
        <h3 style={{ margin: 0, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Maximize size={18} />
          Align Reference Image
        </h3>
        <button 
          onClick={handleClose}
          style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}
        >
          <X size={20} />
        </button>
      </div>

      <div style={buttonGroupStyle}>
        <button 
          style={getButtonStyle(referenceImageAlignMethod === 'interactive')}
          onClick={() => handleMethodChange('interactive')}
        >
          <Move size={16} /> Interactive
        </button>
        <button 
          style={getButtonStyle(referenceImageAlignMethod === 'point-picking')}
          onClick={() => handleMethodChange('point-picking')}
        >
          <MousePointer2 size={16} /> Point Picking
        </button>
      </div>

      {referenceImageAlignMethod === 'interactive' && (
        <>
          <div style={buttonGroupStyle}>
            <button 
              style={getButtonStyle(referenceImageAlignMode === 'translate')}
              onClick={() => setReferenceImageAlignMode('translate')}
            >
              <Move size={14} /> Move
            </button>
            <button 
              style={getButtonStyle(referenceImageAlignMode === 'rotate')}
              onClick={() => setReferenceImageAlignMode('rotate')}
            >
              <RotateCw size={14} /> Rotate
            </button>
            <button 
              style={getButtonStyle(referenceImageAlignMode === 'scale')}
              onClick={() => setReferenceImageAlignMode('scale')}
            >
              <Maximize size={14} /> Scale
            </button>
          </div>
          <div style={instructionsStyle}>
            Use the 3D handles in the scene to adjust the reference image. Scaling is constrained to maintain aspect ratio.
          </div>
          {sceneScaleOptions}
        </>
      )}

      {referenceImageAlignMethod === 'point-picking' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {sceneScaleOptions}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <div style={{ 
              width: '24px', height: '24px', borderRadius: '50%', 
              backgroundColor: pointPickingStep === 'origin' ? '#3b82f6' : (pointPickingStep !== null ? '#10b981' : '#334155'),
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px'
            }}>
              {pointPickingStep !== null ? <Check size={14} /> : '1'}
            </div>
            <span style={{ color: pointPickingStep === 'origin' ? 'white' : '#94a3b8' }}>Pick Origin (0,0)</span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <div style={{ 
              width: '24px', height: '24px', borderRadius: '50%', 
              backgroundColor: pointPickingStep === 'xaxis' ? '#3b82f6' : (pointPickingStep === 'scale-1' || pointPickingStep === 'scale-2' || pointPickingStep === 'scale-distance' ? '#10b981' : '#334155'),
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px'
            }}>
              {pointPickingStep === 'scale-1' || pointPickingStep === 'scale-2' || pointPickingStep === 'scale-distance' ? <Check size={14} /> : '2'}
            </div>
            <span style={{ color: pointPickingStep === 'xaxis' ? 'white' : '#94a3b8' }}>Pick X-Axis Direction</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <div style={{ 
              width: '24px', height: '24px', borderRadius: '50%', 
              backgroundColor: (pointPickingStep === 'scale-1' || pointPickingStep === 'scale-2' || pointPickingStep === 'scale-distance') ? '#3b82f6' : '#334155',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px'
            }}>
              3
            </div>
            <span style={{ color: (pointPickingStep === 'scale-1' || pointPickingStep === 'scale-2' || pointPickingStep === 'scale-distance') ? 'white' : '#94a3b8' }}>Set Scale</span>
          </div>

          <div style={instructionsStyle}>
            {pointPickingStep === 'origin' && "Click on the image to set the (0,0) origin point."}
            {pointPickingStep === 'xaxis' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span>Click a second point on the image to define the positive X-axis direction.</span>
                <button
                  onClick={() => setPointPickingStep('scale-1')}
                  style={{ 
                    padding: '4px 8px', backgroundColor: '#475569', color: 'white', 
                    border: 'none', borderRadius: '4px', cursor: 'pointer', alignSelf: 'flex-start', fontSize: '12px'
                  }}
                >
                  Skip X-Axis Alignment
                </button>
              </div>
            )}
            {pointPickingStep === 'scale-1' && "Click the first point of a known distance to set the scale."}
            {pointPickingStep === 'scale-2' && "Click the second point of the known distance."}
            {pointPickingStep === 'scale-distance' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span>Enter the real-world distance between the two points (in meters):</span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input 
                    type="number" 
                    value={scaleDistanceInput}
                    onChange={(e) => setScaleDistanceInput(e.target.value)}
                    style={{ 
                      flex: 1, padding: '6px', borderRadius: '4px', border: '1px solid #475569', 
                      backgroundColor: '#0f172a', color: 'white' 
                    }}
                  />
                  <button 
                    onClick={handleApplyScaleDistance}
                    style={{ 
                      padding: '6px 12px', backgroundColor: '#3b82f6', color: 'white', 
                      border: 'none', borderRadius: '4px', cursor: 'pointer' 
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
          {pointPickingStep !== 'origin' && (
            <button 
              onClick={handleUndo}
              style={{
                width: '100%',
                padding: '8px',
                backgroundColor: 'transparent',
                color: '#ef4444',
                border: '1px solid #ef4444',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 500,
                marginTop: '5px'
              }}
            >
              Undo Last Click
            </button>
          )}
        </div>
      )}

      <button 
        onClick={handleClose}
        style={{
          width: '100%',
          padding: '10px',
          backgroundColor: '#475569',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          fontWeight: 500,
          marginTop: '5px'
        }}
      >
        Done
      </button>
    </div>
  );
};

export default ReferenceImageAlignmentPanel;
